<template>
  <div v-if="asset" class="bring-capacity">
    <p :class="['bring-capacity__title', 'p1']">{{ t("title") }}</p>

    <div v-if="hasLegacyScript" class="bring-capacity__grid">
      <div :class="['bring-capacity__grid-item', 'bring-capacity__grid-item--icon']">
        <v-icon>status_info_small</v-icon>
      </div>

      <p :class="['bring-capacity__text', 'bring-capacity__grid-item', 'p2']">
        {{ t("hasLegacyScript") }}
      </p>
    </div>

    <v-label>
      <span>{{ t("managedBy.label") }}</span>

      <v-select
        @change="registerCapacity"
        v-model="managedBy"
        :loading="loader.on"
        :readonly="loader.on"
        :items="managerOptions"
        :disabled="hasLimitedCapacity || hasLegacyScript"
        hide-details
      />
    </v-label>

    <v-label v-if="isManagedBy(CapacityReservationType.BRING_YOUR_OWN)">
      <span>{{ t("managedBy.reservationId") }}</span>

      <v-select
        @change="registerCapacity"
        v-model="capacityReservation"
        :items="capacityReservations"
        :loading="loader.on"
        :readonly="loader.on"
        :disabled="hasLimitedCapacity || hasLegacyScript"
        hide-details
      />
    </v-label>

    <div v-if="isManagedBy(CapacityReservationType.BRING_YOUR_OWN)" class="bring-capacity__grid">
      <div :class="['bring-capacity__grid-item', 'bring-capacity__grid-item--icon']">
        <v-icon :color="capacityReservations.length === 0 ? 'warning' : ''">status_info_small</v-icon>
      </div>

      <p
        :class="[
          'bring-capacity__text',
          capacityReservations.length === 0 && 'bring-capacity__text--warning',
          'bring-capacity__grid-item',
          'p2'
        ]"
      >
        {{ capacityReservations.length === 0 ? t("noReservations") : t("availableReservations") }}
      </p>
    </div>

    <div v-if="hasLimitedCapacity && !hasLegacyScript" class="bring-capacity__grid">
      <div :class="['bring-capacity__grid-item', 'bring-capacity__grid-item--icon']">
        <v-icon>status_info_small</v-icon>
      </div>

      <p :class="['bring-capacity__text', 'bring-capacity__grid-item', 'p2']">
        {{ t("hasLimitedCapacity") }}
      </p>
    </div>
  </div>
</template>

<script lang="ts">
import { useStore } from "@/composable/useStore"
import { defineComponent, computed, ref, PropType, watch } from "@vue/composition-api"
import { type WorkspaceTemplate, type IWorkspaceAsset, type Service, CapacityReservationType } from "@/types/workspace"
import { createTranslationModule } from "@/plugins/i18n"
import Loader from "@/utils/loader"
import "../../scss/_bring-own-capacity.scss"

export default defineComponent({
  name: "BringOwnCapacity",
  props: {
    app: {
      type: Object as PropType<IWorkspaceAsset | Service>,
      required: true
    },
    workspace: {
      type: Object as PropType<WorkspaceTemplate>,
      required: true
    }
  },
  setup(props, ctx) {
    const { spaceModule } = useStore()
    const t = createTranslationModule("WorkspaceEdit.bringOwnCapacity")
    const managedBy = ref<`${CapacityReservationType}`>(CapacityReservationType.VIZ_NOW_MANAGED)
    const capacityReservation = ref<string | null>(null)
    const loader = Loader()

    const asset = computed(() => {
      if (props.app._discriminator === "asset") {
        return props.app as IWorkspaceAsset
      }

      return null
    })

    const managerOptions = computed(() => {
      return [
        {
          text: t("managedBy.VizNowManaged"),
          value: CapacityReservationType.VIZ_NOW_MANAGED
        },
        {
          text: t("managedBy.BringYourOwn"),
          value: CapacityReservationType.BRING_YOUR_OWN
        }
      ]
    })

    const hasLimitedCapacity = computed(() => {
      const { cloudZone, cloudZoneAZ } = props.workspace
      return !cloudZone || !cloudZoneAZ
    })

    const hasLegacyScript = computed(() => {
      const { targetCapacityEnabled } = props.workspace
      return !targetCapacityEnabled
    })

    const capacityReservations = computed(() => {
      if (asset.value) {
        return spaceModule.spaceCapacityReservations.map(reservation => {
          return {
            text: `${reservation.capacityReservationId} (${reservation.instanceType})`,
            value: reservation.capacityReservationId
          }
        })
      }

      return []
    })

    const isManagedBy = (manager: CapacityReservationType) => {
      return managedBy.value === manager
    }

    const registerCapacity = () => {
      if (asset.value) {
        const needsCapacityReservation = isManagedBy(CapacityReservationType.BRING_YOUR_OWN)

        if (needsCapacityReservation) {
          if (capacityReservation.value) {
            ctx.emit("update:capacity-reservation", {
              ...asset.value.capacityReservation,
              type: managedBy.value,
              reservationId: capacityReservation.value
            })
          }
        } else {
          ctx.emit("update:capacity-reservation", {
            ...asset.value.capacityReservation,
            type: managedBy.value,
            reservationId: null
          })
        }
      }
    }

    const _fetchReservationIds = () => {
      loader.run(async () => {
        await spaceModule.getAvailableCapacityReservations(props.workspace.id, props.app.id)
        capacityReservation.value = asset.value?.capacityReservation?.reservationId || null
      })
    }

    const _defineReservation = () => {
      if (asset.value?.capacityReservation) {
        managedBy.value = asset.value.capacityReservation.type
      } else {
        managedBy.value = CapacityReservationType.VIZ_NOW_MANAGED
      }

      if (isManagedBy(CapacityReservationType.BRING_YOUR_OWN)) {
        _fetchReservationIds()
      } else {
        registerCapacity()
      }
    }
    _defineReservation()

    watch(managedBy, manager => {
      if (manager === CapacityReservationType.BRING_YOUR_OWN) {
        _fetchReservationIds()
      }
    })

    return {
      t,
      CapacityReservationType,
      hasLegacyScript,
      hasLimitedCapacity,
      loader,
      capacityReservation,
      registerCapacity,
      asset,
      capacityReservations,
      isManagedBy,
      managedBy,
      managerOptions
    }
  }
})
</script>
